import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot, Play, Pause, Zap, FileText, Plus, Trash2, GripVertical,
  Check, X, AlertCircle, Settings, List, BarChart3, RefreshCw,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { SITE_NAME } from '@/config/site';

interface BlogAgentConfig {
  id: string;
  is_active: boolean;
  frequency: string;
  preferred_days: number[];
  publish_hour: number;
  timezone: string;
  ai_provider: string | null;
  prompt_context: string | null;
  author_byline: string | null;
  topic_mode: string;
  preferred_categories: string[] | null;
  review_mode: boolean;
  review_delay_hours: number;
  last_generated_at: string | null;
  posts_generated: number;
}

interface TopicQueueItem {
  id: string;
  topic: string;
  notes: string | null;
  target_category_id: string | null;
  status: string;
  used_by_post_id: string | null;
  post_title?: string;
  post_slug?: string;
  priority: number;
  created_at: string;
}

interface DashboardData {
  config: BlogAgentConfig | null;
  nextRunAt: string | null;
  stats: {
    totalGenerated: number;
    totalFailed: number;
    thisMonth: number;
    pendingTopics: number;
  };
  recentLog: Array<{
    id: string;
    topic_used: string;
    topic_source: string;
    status: string;
    error_message: string | null;
    generation_time_ms: number;
    post_title?: string;
    post_slug?: string;
    created_at: string;
  }>;
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: '2_per_week', label: '2x per week' },
  { value: '1_per_week', label: 'Weekly' },
  { value: 'every_3_days', label: 'Every 3 days' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TOPIC_MODE_OPTIONS = [
  { value: 'auto', label: 'Auto (AI picks topics)' },
  { value: 'queue', label: 'Queue only (use my topics)' },
  { value: 'mixed', label: 'Mixed (queue first, then auto)' },
];

export default function BlogAgent() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'settings' | 'topics'>('dashboard');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [config, setConfig] = useState<BlogAgentConfig | null>(null);
  const [topics, setTopics] = useState<TopicQueueItem[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Topic form
  const [newTopic, setNewTopic] = useState('');
  const [newTopicNotes, setNewTopicNotes] = useState('');
  const [bulkTopics, setBulkTopics] = useState('');
  const [showBulkAdd, setShowBulkAdd] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await api.get<DashboardData>('/admin/blog-agent/dashboard');
      setDashboard(data);
      setConfig(data.config);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    }
  }, []);

  const fetchTopics = useCallback(async () => {
    try {
      const data = await api.get<{ topics: TopicQueueItem[] }>('/admin/blog-agent/topics');
      setTopics(data.topics);
    } catch (err: any) {
      console.error('Failed to load topics:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await api.get<{ categories: BlogCategory[] }>('/admin/blog/categories');
      setCategories(data.categories);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchDashboard(), fetchTopics(), fetchCategories()]).finally(() => setLoading(false));
  }, [fetchDashboard, fetchTopics, fetchCategories]);

  const showMessage = (msg: string, isError = false) => {
    if (isError) { setError(msg); setSuccess(null); }
    else { setSuccess(msg); setError(null); }
    setTimeout(() => { setError(null); setSuccess(null); }, 4000);
  };

  const handleToggle = async () => {
    try {
      const data = await api.put<{ config: BlogAgentConfig }>('/admin/blog-agent/config/toggle', {});
      setConfig(data.config);
      if (dashboard) setDashboard({ ...dashboard, config: data.config });
      showMessage(data.config.is_active ? 'Blog agent activated' : 'Blog agent paused');
    } catch (err: any) {
      showMessage(err.message || 'Failed to toggle agent', true);
    }
  };

  const handleSaveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const data = await api.put<{ config: BlogAgentConfig }>('/admin/blog-agent/config', {
        frequency: config.frequency,
        preferred_days: config.preferred_days,
        publish_hour: config.publish_hour,
        timezone: config.timezone,
        ai_provider: config.ai_provider,
        prompt_context: config.prompt_context,
        author_byline: config.author_byline,
        topic_mode: config.topic_mode,
        preferred_categories: config.preferred_categories,
        review_mode: config.review_mode,
        review_delay_hours: config.review_delay_hours,
      });
      setConfig(data.config);
      showMessage('Settings saved');
    } catch (err: any) {
      showMessage(err.message || 'Failed to save settings', true);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    try {
      await api.post('/admin/blog-agent/generate-now', {});
      showMessage('Blog post generation started! Check back in a moment.');
      setTimeout(fetchDashboard, 10000); // Refresh after 10s
    } catch (err: any) {
      showMessage(err.message || 'Failed to start generation', true);
    } finally {
      setGenerating(false);
    }
  };

  const handleAddTopic = async () => {
    if (!newTopic.trim()) return;
    try {
      await api.post('/admin/blog-agent/topics', {
        topic: newTopic.trim(),
        notes: newTopicNotes.trim() || undefined,
      });
      setNewTopic('');
      setNewTopicNotes('');
      fetchTopics();
      showMessage('Topic added');
    } catch (err: any) {
      showMessage(err.message || 'Failed to add topic', true);
    }
  };

  const handleBulkAdd = async () => {
    const lines = bulkTopics.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    try {
      await api.post('/admin/blog-agent/topics/bulk', { topics: lines });
      setBulkTopics('');
      setShowBulkAdd(false);
      fetchTopics();
      showMessage(`${lines.length} topics added`);
    } catch (err: any) {
      showMessage(err.message || 'Failed to add topics', true);
    }
  };

  const handleDeleteTopic = async (id: string) => {
    try {
      await api.delete(`/admin/blog-agent/topics/${id}`);
      fetchTopics();
    } catch (err: any) {
      showMessage(err.message || 'Failed to delete topic', true);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  const tabs = [
    { key: 'dashboard' as const, label: 'Dashboard', icon: BarChart3 },
    { key: 'settings' as const, label: 'Settings', icon: Settings },
    { key: 'topics' as const, label: 'Topic Queue', icon: List },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bot className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text-primary">Blog Agent</h1>
          {config && (
            <Badge variant={config.is_active ? 'green' : 'gray'}>
              {config.is_active ? 'Active' : 'Paused'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggle}
            disabled={!config}
          >
            {config?.is_active ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
            {config?.is_active ? 'Pause' : 'Activate'}
          </Button>
          <Button
            size="sm"
            onClick={handleGenerateNow}
            disabled={generating}
          >
            <Zap className="w-4 h-4 mr-1" />
            {generating ? 'Generating...' : 'Generate Now'}
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />{error}
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />{success}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.key === 'topics' && dashboard?.stats.pendingTopics ? (
              <span className="bg-primary/10 text-primary text-xs px-1.5 py-0.5 rounded-full">
                {dashboard.stats.pendingTopics}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <DashboardTab dashboard={dashboard} onRefresh={fetchDashboard} />}
      {activeTab === 'settings' && config && (
        <SettingsTab
          config={config}
          categories={categories}
          saving={saving}
          onChange={setConfig}
          onSave={handleSaveConfig}
        />
      )}
      {activeTab === 'topics' && (
        <TopicsTab
          topics={topics}
          newTopic={newTopic}
          newTopicNotes={newTopicNotes}
          bulkTopics={bulkTopics}
          showBulkAdd={showBulkAdd}
          onNewTopicChange={setNewTopic}
          onNewTopicNotesChange={setNewTopicNotes}
          onBulkTopicsChange={setBulkTopics}
          onToggleBulkAdd={() => setShowBulkAdd(!showBulkAdd)}
          onAddTopic={handleAddTopic}
          onBulkAdd={handleBulkAdd}
          onDeleteTopic={handleDeleteTopic}
        />
      )}
    </div>
  );
}

// ── Dashboard Tab ──────────────────────────────────────────────────

function DashboardTab({ dashboard, onRefresh }: { dashboard: DashboardData | null; onRefresh: () => void }) {
  if (!dashboard) return null;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-text-muted">Total Generated</p>
          <p className="text-2xl font-bold text-text-primary">{dashboard.stats.totalGenerated}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-text-muted">This Month</p>
          <p className="text-2xl font-bold text-text-primary">{dashboard.stats.thisMonth}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-text-muted">Failed</p>
          <p className="text-2xl font-bold text-red-600">{dashboard.stats.totalFailed}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-text-muted">Queued Topics</p>
          <p className="text-2xl font-bold text-text-primary">{dashboard.stats.pendingTopics}</p>
        </Card>
      </div>

      {/* Status Info */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-text-muted">
              Last generated: {dashboard.config?.last_generated_at
                ? new Date(dashboard.config.last_generated_at).toLocaleString()
                : 'Never'}
            </p>
            <p className="text-sm text-text-muted">
              Next scheduled: {dashboard.nextRunAt
                ? new Date(dashboard.nextRunAt).toLocaleString()
                : 'Not scheduled'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
        </div>
      </Card>

      {/* Recent Log */}
      <Card>
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-text-primary">Recent Activity</h3>
        </div>
        {dashboard.recentLog.length === 0 ? (
          <div className="p-8 text-center text-text-muted">
            <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No activity yet. Activate the agent or click "Generate Now" to get started.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {dashboard.recentLog.map(entry => (
              <div key={entry.id} className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {entry.status === 'success' ? (
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-red-600 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-text-primary truncate">
                      {entry.post_title || entry.topic_used}
                    </span>
                    <Badge variant={entry.topic_source === 'queue' ? 'blue' : 'gray'} className="text-xs">
                      {entry.topic_source}
                    </Badge>
                  </div>
                  {entry.error_message && (
                    <p className="text-xs text-red-600 mt-1 ml-6">{entry.error_message}</p>
                  )}
                </div>
                <div className="text-xs text-text-muted text-right flex-shrink-0 ml-4">
                  <p>{new Date(entry.created_at).toLocaleDateString()}</p>
                  {entry.generation_time_ms && (
                    <p>{(entry.generation_time_ms / 1000).toFixed(1)}s</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Settings Tab ───────────────────────────────────────────────────

function SettingsTab({
  config, categories, saving, onChange, onSave,
}: {
  config: BlogAgentConfig;
  categories: BlogCategory[];
  saving: boolean;
  onChange: (c: BlogAgentConfig) => void;
  onSave: () => void;
}) {
  const showDays = ['2_per_week', '1_per_week', 'biweekly'].includes(config.frequency);

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-5">
        <h3 className="font-semibold text-text-primary">Schedule</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Frequency</label>
            <Select
              value={config.frequency}
              onChange={(e) => onChange({ ...config, frequency: e.target.value })}
              options={FREQUENCY_OPTIONS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Publish Hour</label>
            <Select
              value={String(config.publish_hour)}
              onChange={(e) => onChange({ ...config, publish_hour: parseInt(e.target.value) })}
              options={Array.from({ length: 24 }, (_, i) => ({
                value: String(i),
                label: `${i.toString().padStart(2, '0')}:00`,
              }))}
            />
          </div>
        </div>

        {showDays && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Preferred Days</label>
            <div className="flex gap-2">
              {DAY_NAMES.map((name, i) => (
                <button
                  key={i}
                  onClick={() => {
                    const days = config.preferred_days.includes(i)
                      ? config.preferred_days.filter(d => d !== i)
                      : [...config.preferred_days, i].sort();
                    onChange({ ...config, preferred_days: days });
                  }}
                  className={`w-10 h-10 rounded-lg text-xs font-medium transition-colors ${
                    config.preferred_days.includes(i)
                      ? 'bg-primary text-white'
                      : 'bg-surface-secondary text-text-muted hover:bg-surface-tertiary'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 space-y-5">
        <h3 className="font-semibold text-text-primary">Content</h3>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Topic Mode</label>
          <Select
            value={config.topic_mode}
            onChange={(e) => onChange({ ...config, topic_mode: e.target.value })}
            options={TOPIC_MODE_OPTIONS}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Preferred Categories
            <span className="text-text-muted font-normal ml-1">(optional)</span>
          </label>
          <div className="flex flex-wrap gap-2 mt-1">
            {categories.map(cat => {
              const selected = config.preferred_categories?.includes(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    const current = config.preferred_categories || [];
                    const next = selected
                      ? current.filter(id => id !== cat.id)
                      : [...current, cat.id];
                    onChange({ ...config, preferred_categories: next.length > 0 ? next : null });
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    selected
                      ? 'bg-primary text-white'
                      : 'bg-surface-secondary text-text-muted hover:bg-surface-tertiary'
                  }`}
                >
                  {cat.name}
                </button>
              );
            })}
            {categories.length === 0 && (
              <p className="text-sm text-text-muted">No blog categories found. <Link to="/admin/blog/categories" className="text-primary hover:underline">Create some first.</Link></p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Prompt Context
            <span className="text-text-muted font-normal ml-1">(guides the AI's topic selection and writing style)</span>
          </label>
          <textarea
            value={config.prompt_context || ''}
            onChange={(e) => onChange({ ...config, prompt_context: e.target.value || null })}
            placeholder="e.g., Focus on beginner-friendly gardening tips. Use a warm, encouraging tone. Mention seasonal planting guides."
            className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-y min-h-[80px] bg-surface text-text-primary"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Author byline
            <span className="text-text-muted font-normal ml-1">(shown as the author on generated posts)</span>
          </label>
          <Input
            value={config.author_byline || ''}
            onChange={(e) => onChange({ ...config, author_byline: e.target.value || null })}
            placeholder={`${SITE_NAME} Team`}
          />
          <p className="text-xs text-text-muted mt-1">
            Leave blank to use “{SITE_NAME} Team”.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <h3 className="font-semibold text-text-primary">Autonomy</h3>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.review_mode}
            onChange={(e) => onChange({ ...config, review_mode: e.target.checked })}
            className="w-4 h-4 rounded border-border"
          />
          <div>
            <span className="text-sm font-medium text-text-primary">Review mode</span>
            <p className="text-xs text-text-muted">
              When enabled, posts are saved as drafts first and auto-publish after a delay.
              When disabled, posts are published immediately.
            </p>
          </div>
        </label>

        {config.review_mode && (
          <div className="ml-7">
            <label className="block text-sm font-medium text-text-primary mb-1">Auto-publish delay (hours)</label>
            <Input
              type="number"
              min={1}
              max={168}
              value={config.review_delay_hours}
              onChange={(e) => onChange({ ...config, review_delay_hours: parseInt(e.target.value) || 24 })}
              className="w-32"
            />
            <p className="text-xs text-text-muted mt-1">Posts will auto-publish after this delay unless you edit or delete them.</p>
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}

// ── Topics Tab ─────────────────────────────────────────────────────

function TopicsTab({
  topics, newTopic, newTopicNotes, bulkTopics, showBulkAdd,
  onNewTopicChange, onNewTopicNotesChange, onBulkTopicsChange,
  onToggleBulkAdd, onAddTopic, onBulkAdd, onDeleteTopic,
}: {
  topics: TopicQueueItem[];
  newTopic: string;
  newTopicNotes: string;
  bulkTopics: string;
  showBulkAdd: boolean;
  onNewTopicChange: (v: string) => void;
  onNewTopicNotesChange: (v: string) => void;
  onBulkTopicsChange: (v: string) => void;
  onToggleBulkAdd: () => void;
  onAddTopic: () => void;
  onBulkAdd: () => void;
  onDeleteTopic: (id: string) => void;
}) {
  const pendingTopics = topics.filter(t => t.status === 'pending');
  const usedTopics = topics.filter(t => t.status !== 'pending');

  return (
    <div className="space-y-6">
      {/* Add Topic */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-text-primary">Add Topic</h3>
          <button
            onClick={onToggleBulkAdd}
            className="text-sm text-primary hover:underline"
          >
            {showBulkAdd ? 'Single add' : 'Bulk add'}
          </button>
        </div>

        {showBulkAdd ? (
          <div className="space-y-2">
            <textarea
              value={bulkTopics}
              onChange={(e) => onBulkTopicsChange(e.target.value)}
              placeholder="One topic per line..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-y min-h-[100px] bg-surface text-text-primary"
              rows={5}
            />
            <Button size="sm" onClick={onBulkAdd} disabled={!bulkTopics.trim()}>
              <Plus className="w-4 h-4 mr-1" />Add All
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              value={newTopic}
              onChange={(e) => onNewTopicChange(e.target.value)}
              placeholder="Blog post topic..."
              onKeyDown={(e) => e.key === 'Enter' && onAddTopic()}
            />
            <Input
              value={newTopicNotes}
              onChange={(e) => onNewTopicNotesChange(e.target.value)}
              placeholder="Notes/context (optional)"
            />
            <Button size="sm" onClick={onAddTopic} disabled={!newTopic.trim()}>
              <Plus className="w-4 h-4 mr-1" />Add Topic
            </Button>
          </div>
        )}
      </Card>

      {/* Pending Topics */}
      <Card>
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-text-primary">
            Pending Topics ({pendingTopics.length})
          </h3>
        </div>
        {pendingTopics.length === 0 ? (
          <div className="p-8 text-center text-text-muted">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No pending topics. {topics.length === 0 ? 'Add some above or let the AI pick topics automatically.' : ''}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {pendingTopics.map(topic => (
              <div key={topic.id} className="p-4 flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-text-muted flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">{topic.topic}</p>
                  {topic.notes && <p className="text-xs text-text-muted mt-0.5">{topic.notes}</p>}
                </div>
                <Badge variant="gray" className="text-xs">P{topic.priority}</Badge>
                <button
                  onClick={() => onDeleteTopic(topic.id)}
                  className="text-text-muted hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Used Topics */}
      {usedTopics.length > 0 && (
        <Card>
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-text-muted">Used Topics</h3>
          </div>
          <div className="divide-y divide-border">
            {usedTopics.map(topic => (
              <div key={topic.id} className="p-4 flex items-center gap-3 opacity-60">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary">{topic.topic}</p>
                  {topic.post_title && (
                    <Link
                      to={`/admin/blog/${topic.used_by_post_id}`}
                      className="text-xs text-primary hover:underline"
                    >
                      {topic.post_title}
                    </Link>
                  )}
                </div>
                <Badge variant="green" className="text-xs">{topic.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
